/**
 * schedule_modal_controller.js — quote/job "Schedule" modal + its FullCalendar (Stimulus).
 *
 * Ports static/js/schedule_event.js (the modal scheduler shown from quote/job detail
 * pages and scheduled-date badges) to an element-scoped controller hosted on the
 * #scheduleEventModal element (rendered in base.html on every page).
 *
 * Lifecycle (replaces schedule_event.js's hand-rolled document-flag guards +
 * turbo:before-cache at line 1504):
 *   connect()    re-exposes the legacy window.* entry points (so the many
 *                template call sites keep working with no markup change),
 *                attaches the delegated document click handler + modal button
 *                wiring, and registers a connect-scoped turbo:before-cache
 *                teardown. The instance owning add/remove means the document
 *                listeners can't stack across Turbo navigations.
 *   disconnect() removes those document listeners and tears the calendar down.
 *   turbo:before-cache destroys the lazily-built calendar, closes the modal and
 *                resets state so the cached snapshot ships nothing live (the
 *                original InvalidStateError-on-restore guard). FullCalendar is a
 *                PageLoader-injected page script; the calendar is built lazily on
 *                modal open (shown.bs.modal), not on connect, so connect needs no
 *                FullCalendar-loaded deferral.
 *
 * State that was on window.schedule* is now controller-scoped (this.calendar,
 * this.selectedDate, ...). The window.openScheduleModal/...-style FUNCTIONS stay
 * exposed (bound to this instance) because quote/job detail templates, the
 * scheduled_date_link macro and badges still call them directly.
 *
 * Calendar instance/state SSOT: this controller. Event data: existing
 * /calendar + /api/v1/calendar endpoints (no hardcoded events).
 */

import { Controller } from "@hotwired/stimulus";
import { eventTitleFor } from "../../calendar/create-event.js";
import { readWeekendsPref, rememberWeekendsPref } from "../../calendar/weekends.js";
import { crewFromAssignees, readAssigneeSelection, seedAssigneePicker } from "../../utils/assignee-selection.js";
import { addWorkingDays, formatLocalDatetime, visitTiming } from "./_visit_time.js";
import { formatDuration } from "./duration_dial_controller.js";

    /**
     * Headers for calendar-event mutation fetches (create / reschedule / delete).
     * X-Tab-Id (SSE originator-skip) is added by the global window.fetch wrapper in
     * tab-session.js. These web routes are @csrf.exempt. X-Page-Path carries the
     * acting tab's real location so the detail page's scheduled-events card repaints
     * reliably even when Referer doesn't match the entity (e.g. from /calendar).
     */
    function scheduleMutationHeaders() {
        return {
            'Content-Type': 'application/json',
            'Accept': 'text/vnd.turbo-stream.html',
            'X-Page-Path': window.location.pathname
        };
    }

    function roundToHalfHour(date) {
        const result = new Date(date);
        const mins = Math.round(result.getMinutes() / 30) * 30;
        result.setMinutes(0, 0, 0);
        result.setMinutes(mins); // setMinutes(60) rolls into the next hour
        return result;
    }

    // The calendar page's quick-schedule modal (static/js/calendar/schedule-modal.js,
    // in the calendar bundle) calls window.addWorkingDays for its multi-day span maths.
    // The legacy schedule_event.js exposed it implicitly as a top-level function in a
    // non-module script; this IIFE would hide it, so re-expose it explicitly. This
    // script loads (base.html, defer) before PageLoader injects the calendar bundle.
    window.addWorkingDays = addWorkingDays;

    // Global toast fallback, kept available app-wide (matches the legacy module).
    if (typeof window.showToast !== 'function') {
        window.showToast = function(message, type = 'info') {
            let container = document.getElementById('toast-container');
            if (!container) {
                container = document.createElement('div');
                container.id = 'toast-container';
                container.className = 'toast-container position-fixed top-0 end-0 p-3';
                container.style.zIndex = '1100';
                container.setAttribute('data-turbo-temporary', '');
                document.body.appendChild(container);
            }
            const bgClass = type === 'success' ? 'bg-success' : type === 'error' ? 'bg-danger' : 'bg-info';
            const toastHtml = `
                <div class="toast align-items-center text-white ${bgClass} border-0" role="alert">
                    <div class="d-flex">
                        <div class="toast-body">${message}</div>
                        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button>
                    </div>
                </div>
            `;
            container.insertAdjacentHTML('beforeend', toastHtml);
            const toastEl = container.lastElementChild;
            const toast = new bootstrap.Toast(toastEl, { delay: 3000 });
            toast.show();
            toastEl.addEventListener('hidden.bs.toast', () => toastEl.remove());
        };
    }

export default class extends Controller {
        connect() {
            // Instance state (was window.schedule*)
            this.calendar = null;
            this.selectedDate = null;
            this.currentEvent = null;
            this.currentEntityType = null;
            this.currentEntityId = null;
            this.isDeleting = false;       // guard against double-clicks
            this.previewEvent = null;      // temporary multi-day preview
            this.selectionEvent = null;    // draggable/resizable day-view time block
            this.isUpdatingSelection = false; // programmatic writes vs user drags

            // Re-expose legacy entry points (templates / macro / badges call these).
            this._exposeGlobals();

            // Delegated on document (persists across body swaps); removed on disconnect.
            this._onDocClick = this.handleScheduleClick.bind(this);
            document.addEventListener('click', this._onDocClick, true);

            this._onBeforeCache = this.teardownForCache.bind(this);
            document.addEventListener('turbo:before-cache', this._onBeforeCache);

            this.initScheduleModalButtons();
        }

        disconnect() {
            if (this._onDocClick) {
                document.removeEventListener('click', this._onDocClick, true);
                this._onDocClick = null;
            }
            if (this._onBeforeCache) {
                document.removeEventListener('turbo:before-cache', this._onBeforeCache);
                this._onBeforeCache = null;
            }
            this._destroyCalendar();
        }

        _exposeGlobals() {
            window.openScheduleModal = this.openScheduleModal.bind(this);
            window.openScheduleModalFromBadge = this.openScheduleModalFromBadge.bind(this);
            window.showScheduleFormPanel = this.showScheduleFormPanel.bind(this);
            window.showEventDetailsPanel = this.showEventDetailsPanel.bind(this);
            window.deleteScheduleEvent = this.deleteScheduleEvent.bind(this);
            window.startRescheduleEvent = this.startRescheduleEvent.bind(this);
            window.submitScheduleEvent = this.submitScheduleEvent.bind(this);
        }

        _destroyCalendar() {
            if (this.calendar) {
                try { this.calendar.destroy(); } catch (_e) {}
                this.calendar = null;
            }
        }

        // turbo:before-cache: snapshot must hold nothing live.
        teardownForCache() {
            this._destroyCalendar();

            const modal = document.getElementById('scheduleEventModal');
            if (modal) {
                const bsModal = bootstrap.Modal.getInstance(modal);
                if (bsModal) {
                    try { bsModal.hide(); } catch (_e) {}
                }
                modal._scheduleShownAttached = false;
            }

            this.selectedDate = null;
            this.currentEvent = null;
            this.currentEntityType = null;
            this.currentEntityId = null;
            this.isDeleting = false;
            this.previewEvent = null;
            this.selectionEvent = null;
        }

        /**
         * Open the schedule modal with pre-filled values from job/quote context.
         */
        openScheduleModal(entityType, entityId, clientId, clientName, location, defaultTitle, defaultDuration) {
            const scheduleModal = document.getElementById('scheduleEventModal');
            if (!scheduleModal) {
                console.error('[Schedule] Modal element not found in DOM');
                return;
            }

            // Store current entity for highlighting in calendar
            this.currentEntityType = entityType;
            this.currentEntityId = entityId;

            // Reset state
            this.selectedDate = null;
            this.currentEvent = null;
            this._destroyCalendar();

            // Show form panel, hide details panel
            this.showScheduleFormPanel();

            // Set hidden fields
            const entityTypeEl = document.getElementById('scheduleEntityType');
            const entityIdEl = document.getElementById('scheduleEntityId');
            const clientIdEl = document.getElementById('scheduleClientId');
            const titleEl = document.getElementById('scheduleEventTitle');
            const locationEl = document.getElementById('scheduleEventLocation');
            const dateEl = document.getElementById('scheduleEventDate');
            const editEventIdEl = document.getElementById('scheduleEditEventId');

            if (entityTypeEl) entityTypeEl.value = entityType || '';
            if (entityIdEl) entityIdEl.value = entityId || '';
            if (clientIdEl) clientIdEl.value = clientId || '';
            if (titleEl) titleEl.value = defaultTitle || '';
            if (locationEl) locationEl.value = location || '';
            if (dateEl) dateEl.value = '';
            if (editEventIdEl) editEventIdEl.value = '';

            // A fresh open starts with nobody on the visit, so a crew picked
            // for the last job does not carry over to this one.
            seedAssigneePicker(scheduleModal);

            // Set display fields
            const clientNameEl = document.getElementById('scheduleClientName');
            if (clientNameEl) {
                clientNameEl.textContent = clientName || 'Client';
            }

            const locationDisplay = document.getElementById('scheduleLocationDisplay');
            const locationRow = document.getElementById('scheduleLocationRow');
            if (locationDisplay && locationRow) {
                if (location && location.trim()) {
                    const cleanLocation = location.replace(/<br\s*\/?>/gi, ', ').replace(/<[^>]*>/g, '');
                    locationDisplay.textContent = cleanLocation;
                    locationRow.style.display = 'flex';
                } else {
                    locationRow.style.display = 'none';
                }
            }

            // Set default duration
            const durationSelect = document.getElementById('scheduleEventDuration');
            if (durationSelect && defaultDuration) {
                durationSelect.value = defaultDuration.toString();
            }

            // Update modal title
            const modalTitle = document.getElementById('scheduleEventModalLabel');
            if (modalTitle) {
                if (entityType === 'quote') {
                    modalTitle.innerHTML = '<i class="bi bi-calendar-plus me-2"></i>Schedule Site Visit';
                } else if (entityType === 'job') {
                    modalTitle.innerHTML = '<i class="bi bi-calendar-plus me-2"></i>Schedule Job';
                }
            }

            const timeOptionsRow = document.getElementById('timeOptionsRow');
            if (timeOptionsRow) timeOptionsRow.style.display = 'none';

            const daysRow = document.getElementById('daysRow');
            if (daysRow) daysRow.style.display = 'none';

            const notesRow = document.getElementById('notesRow');
            if (notesRow) notesRow.style.display = 'none';

            // Reset days to 1 and clear any stale preview / selection block
            const daysInput = document.getElementById('scheduleEventDays');
            if (daysInput) daysInput.value = 1;
            if (this.previewEvent) {
                try { this.previewEvent.remove(); } catch (_e) {}
                this.previewEvent = null;
            }
            this.clearScheduleSelection();

            // Show event click tip
            const eventClickTip = document.getElementById('eventClickTip');
            if (eventClickTip) eventClickTip.style.display = 'block';

            // The calendar's first datesSet shows the prompt once it has a view to describe
            const stepPrompt = document.getElementById('scheduleStepPrompt');
            if (stepPrompt) stepPrompt.style.display = 'none';

            // Hide footer duration control until a date is selected
            const footerDurationGroup = document.getElementById('footerDurationGroup');
            if (footerDurationGroup) footerDurationGroup.style.display = 'none';

            const submitBtn = document.getElementById('submitScheduleEventBtn');
            if (submitBtn) {
                submitBtn.disabled = true;
                submitBtn.innerHTML = '<i class="bi bi-calendar-check me-1"></i>Schedule';
            }

            // Show footer
            const footer = document.getElementById('scheduleModalFooter');
            if (footer) footer.style.display = 'flex';

            // Clear optional fields
            const timeSelect = document.getElementById('scheduleEventTime');
            if (timeSelect) timeSelect.value = '';

            const descField = document.getElementById('scheduleEventDescription');
            if (descField) descField.value = '';

            // Collapse notes
            const notesCollapse = document.getElementById('scheduleNotesCollapse');
            if (notesCollapse && notesCollapse.classList.contains('show')) {
                notesCollapse.classList.remove('show');
            }

            // Clear the calendar container before showing modal
            const calendarEl = document.getElementById('scheduleCalendar');
            if (calendarEl) {
                calendarEl.innerHTML = '';
            }

            // Show modal using Bootstrap - get existing instance or create new one
            let modal = bootstrap.Modal.getInstance(scheduleModal);
            if (!modal) {
                modal = new bootstrap.Modal(scheduleModal);
            }
            modal.show();
        }

        showScheduleFormPanel() {
            const formPanel = document.getElementById('scheduleFormPanel');
            const detailsPanel = document.getElementById('eventDetailsPanel');
            const footer = document.getElementById('scheduleModalFooter');

            if (formPanel) formPanel.style.display = 'block';
            if (detailsPanel) detailsPanel.style.display = 'none';
            if (footer) footer.style.display = 'flex';
        }

        showEventDetailsPanel(event) {
            const formPanel = document.getElementById('scheduleFormPanel');
            const detailsPanel = document.getElementById('eventDetailsPanel');
            const footer = document.getElementById('scheduleModalFooter');

            if (formPanel) formPanel.style.display = 'none';
            if (detailsPanel) detailsPanel.style.display = 'block';
            if (footer) footer.style.display = 'none';

            // Store current event
            this.currentEvent = event;
            document.getElementById('currentEventId').value = event.id;

            // Populate event details
            const titleEl = document.getElementById('eventDetailTitle');
            if (titleEl) titleEl.textContent = event.title;

            const dateEl = document.getElementById('eventDetailDate');
            if (dateEl) {
                const startDate = new Date(event.start);
                const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
                dateEl.textContent = startDate.toLocaleDateString('en-AU', options);
            }

            const timeEl = document.getElementById('eventDetailTime');
            if (timeEl) {
                if (event.allDay) {
                    timeEl.textContent = 'All Day';
                } else {
                    const startDate = new Date(event.start);
                    const endDate = event.end ? new Date(event.end) : null;
                    const startTime = startDate.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
                    if (endDate) {
                        const endTime = endDate.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
                        timeEl.textContent = `${startTime} - ${endTime}`;
                    } else {
                        timeEl.textContent = startTime;
                    }
                }
            }

            const locationRow = document.getElementById('eventDetailLocationRow');
            const locationEl = document.getElementById('eventDetailLocation');
            let location = event.extendedProps?.location;
            if (locationRow && locationEl) {
                if (location) {
                    location = location.replace(/<br\s*\/?>/gi, ', ').replace(/<[^>]*>/g, '');
                    locationEl.textContent = location;
                    locationRow.style.display = 'block';
                } else {
                    locationRow.style.display = 'none';
                }
            }

            // Populate entity link (quote or job navigation)
            const entityLinkRow = document.getElementById('eventDetailEntityLinkRow');
            const entityLink = document.getElementById('eventDetailEntityLink');
            const entityLinkText = document.getElementById('eventDetailEntityLinkText');
            const quoteId = event.extendedProps?.quote_id;
            const jobId = event.extendedProps?.job_id;

            if (entityLinkRow && entityLink && entityLinkText) {
                if (jobId) {
                    entityLink.href = `/job/${jobId}`;
                    entityLinkText.textContent = 'View Job';
                    entityLinkRow.style.display = 'block';
                } else if (quoteId) {
                    entityLink.href = `/quote/${quoteId}`;
                    entityLinkText.textContent = 'View Quote';
                    entityLinkRow.style.display = 'block';
                } else {
                    entityLinkRow.style.display = 'none';
                }
            }
        }

        /** Remove the draggable day-view selection block, if present. */
        clearScheduleSelection() {
            if (this.selectionEvent) {
                try { this.selectionEvent.remove(); } catch (_e) {}
                this.selectionEvent = null;
            }
        }

        /**
         * Sync Start Time dropdown + hidden duration from a (start, end) pair after a
         * drag/resize. Guarded so the programmatic writes don't loop back.
         */
        syncControlsFromSelection(start, end) {
            this.isUpdatingSelection = true;

            const timeSelect = document.getElementById('scheduleEventTime');
            if (timeSelect) {
                const timeStr = `${start.getHours().toString().padStart(2, '0')}:${start.getMinutes().toString().padStart(2, '0')}`;
                if (Array.from(timeSelect.options).some((opt) => opt.value === timeStr)) {
                    timeSelect.value = timeStr;
                }
            }

            const durationInput = document.getElementById('scheduleEventDuration');
            if (durationInput && end) {
                durationInput.value = Math.max(30, Math.round((end - start) / 60000));
            }

            this.updateDurationVisibility();
            setTimeout(() => { this.isUpdatingSelection = false; }, 50);
        }

        /**
         * Place (or replace) the editable day-view selection block at start..end,
         * snapped to the 30-minute grid, and sync the controls.
         */
        setScheduleSelection(start, end) {
            if (!this.calendar) return;
            if (this.isUpdatingSelection) return;

            const snappedStart = roundToHalfHour(start);
            let durationMinutes = Math.round((end - snappedStart) / 60000 / 30) * 30;
            if (durationMinutes < 30) durationMinutes = 30;
            const snappedEnd = new Date(snappedStart.getTime() + durationMinutes * 60000);

            this.isUpdatingSelection = true;

            this.calendar.unselect(); // drop any leftover selection highlight

            // Mutate an existing block instead of remove+add: keeping the same
            // DOM node lets the CSS inset transition glide it between sizes
            // (e.g. while the duration dial spins) instead of jumping.
            const existing = this.calendar.getEventById('schedule-selection');
            if (existing) {
                existing.setDates(snappedStart, snappedEnd);
                this.selectionEvent = existing;
            } else {
                this.selectionEvent = this.calendar.addEvent({
                    id: 'schedule-selection',
                    title: 'Selected time',
                    start: snappedStart,
                    end: snappedEnd,
                    editable: true,
                    startEditable: true,
                    durationEditable: true,
                    backgroundColor: '#0d6efd',
                    borderColor: '#0d6efd',
                    classNames: ['schedule-selection-event']
                });
            }

            // Mirror the block into the form controls
            const timeSelect = document.getElementById('scheduleEventTime');
            if (timeSelect) {
                const timeStr = `${snappedStart.getHours().toString().padStart(2, '0')}:${snappedStart.getMinutes().toString().padStart(2, '0')}`;
                if (Array.from(timeSelect.options).some((opt) => opt.value === timeStr)) {
                    timeSelect.value = timeStr;
                }
            }
            const durationInput = document.getElementById('scheduleEventDuration');
            if (durationInput) durationInput.value = durationMinutes;
            this.updateDurationVisibility();

            setTimeout(() => { this.isUpdatingSelection = false; }, 50);
        }

        /** Place the selection block at startDateTime using the current duration. */
        updateCalendarSelectionWithDuration(startDateTime) {
            if (!this.calendar) return;
            const durationInput = document.getElementById('scheduleEventDuration');
            const durationMinutes = durationInput ? parseInt(durationInput.value) || 30 : 30;
            const start = roundToHalfHour(startDateTime);
            const end = new Date(start.getTime() + durationMinutes * 60000);
            this.setScheduleSelection(start, end);
        }

        /** Background preview event spanning the selected working-day range. */
        updateMultiDayPreview() {
            if (this.previewEvent) {
                try { this.previewEvent.remove(); } catch (_e) {}
                this.previewEvent = null;
            }

            if (!this.calendar || !this.selectedDate) return;

            const daysInput = document.getElementById('scheduleEventDays');
            const numDays = daysInput ? (parseInt(daysInput.value) || 1) : 1;
            if (numDays <= 1) return;

            // Multi-day spans use the all-day background preview, not the timed block
            this.clearScheduleSelection();

            const startDate = new Date(`${this.selectedDate}T00:00:00`);
            const lastDay = addWorkingDays(startDate, numDays);

            // FullCalendar all-day end is exclusive
            const exclusiveEnd = new Date(lastDay);
            exclusiveEnd.setDate(exclusiveEnd.getDate() + 1);

            this.previewEvent = this.calendar.addEvent({
                title: `${numDays} days`,
                start: startDate,
                end: exclusiveEnd,
                allDay: true,
                display: 'background',
                backgroundColor: '#0d6efd',
                classNames: ['schedule-preview-event']
            });
        }

        /** Update footer Duration dropup, weekends-skip note and step prompt from the current selection. */
        updateDurationVisibility() {
            const timeSelect = document.getElementById('scheduleEventTime');
            const footerDurationGroup = document.getElementById('footerDurationGroup');
            const footerDurationText = document.getElementById('footerDurationText');
            const durationSelect = document.getElementById('scheduleEventDuration');
            const daysInput = document.getElementById('scheduleEventDays');
            const daysSkipNote = document.getElementById('daysSkipNote');

            const numDays = daysInput ? (parseInt(daysInput.value) || 1) : 1;
            const isMultiDay = numDays > 1;
            const hasTime = timeSelect && timeSelect.value !== '';

            if (daysSkipNote) {
                daysSkipNote.style.display = isMultiDay ? '' : 'none';
            }

            if (footerDurationText) {
                if (isMultiDay) {
                    footerDurationText.textContent = `${numDays}d`;
                } else if (hasTime && durationSelect) {
                    footerDurationText.textContent = formatDuration(parseInt(durationSelect.value) || 30);
                } else {
                    footerDurationText.textContent = 'All Day';
                }
            }

            if (footerDurationGroup && this.selectedDate) {
                const btn = footerDurationGroup.querySelector('button');
                footerDurationGroup.style.display = '';
                if (hasTime && !isMultiDay) {
                    btn.disabled = false;
                    btn.classList.add('dropdown-toggle');
                } else {
                    btn.disabled = true;
                    btn.classList.remove('dropdown-toggle');
                }
            }

            this.updateStepPrompt();
        }

        updateFooterDurationText() {
            this.updateDurationVisibility();
        }

        /**
         * Show the step prompt with what to pick next, or hide it when nothing is.
         * The duration dial stays disabled until a start time exists, so without
         * this a user between steps sees a greyed-out control and no reason why.
         */
        updateStepPrompt() {
            const prompt = document.getElementById('scheduleStepPrompt');
            const promptText = document.getElementById('scheduleStepPromptText');
            if (!prompt || !promptText || !this.calendar) return;

            const text = this._stepPromptText();
            promptText.textContent = text;
            prompt.style.display = text ? 'block' : 'none';
        }

        /** The prompt for the current step, or '' once there is nothing left to pick. */
        _stepPromptText() {
            const isReschedule = !!document.getElementById('scheduleEditEventId')?.value;
            const numDays = parseInt(document.getElementById('scheduleEventDays')?.value) || 1;
            const hasTime = !!document.getElementById('scheduleEventTime')?.value;
            const newWord = isReschedule ? 'new ' : '';

            // A multi-day visit is all-day: once its date is set there is no time to pick.
            if (numDays > 1) return '';
            if (this.isDayGridView(this.calendar.view.type)) return `Pick a ${newWord}date`;
            if (hasTime) return '';
            return `Select a ${newWord}start time: tap a time slot, or use Start Time below`;
        }

        /** Week and month share all day-grid interaction (click day -> pick time). */
        isDayGridView(viewType) {
            return viewType === 'dayGridWeek' || viewType === 'dayGridMonth';
        }

        /**
         * Run build once FullCalendar is on the page. Quote and job pages carry
         * this modal but not FullCalendar (about 100 KB): it is fetched the first
         * time the modal opens, through the 'schedule-event' PageLoader module
         * registered in base.html, so a page that is only read never pays for
         * it. A failed load still builds, and initScheduleCalendar reports it.
         */
        _whenCalendarLoaded(modal, build) {
            if (typeof FullCalendar !== 'undefined' || !window.PageLoader) {
                build();
                return;
            }
            const calendarEl = document.getElementById('scheduleCalendar');
            if (calendarEl) {
                calendarEl.innerHTML = '<div class="text-muted small p-3">Loading calendar…</div>';
            }
            window.PageLoader.load('schedule-event')
                .catch(() => {})
                .then(() => {
                    if (modal.classList.contains('show')) build();
                });
        }

        /** Initialize FullCalendar in the modal. */
        initScheduleCalendar() {
            const self = this;
            const calendarEl = document.getElementById('scheduleCalendar');
            if (!calendarEl) {
                console.error('[Schedule] Calendar element not found');
                return;
            }

            this._destroyCalendar();
            calendarEl.innerHTML = '';

            if (typeof FullCalendar === 'undefined') {
                console.error('[Schedule] FullCalendar not loaded');
                calendarEl.innerHTML = '<div class="alert alert-danger">Calendar failed to load. Please refresh the page.</div>';
                return;
            }

            // Captured once at build time for event highlighting (matches legacy).
            const currentEntityType = this.currentEntityType;
            const currentEntityId = this.currentEntityId;

            // validRange must reach back to Monday or the week view renders the
            // already-passed weekdays as blank dead columns (out-of-range days
            // are excluded from the grid, not just disabled). Past days stay
            // unselectable via selectAllow + the selectScheduleDate guard.
            const startOfToday = new Date();
            startOfToday.setHours(0, 0, 0, 0);
            const weekStart = new Date(startOfToday);
            weekStart.setDate(startOfToday.getDate() - ((startOfToday.getDay() + 6) % 7));
            const toLocalDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

            this._lastViewType = null;
            this.calendar = new FullCalendar.Calendar(calendarEl, {
                initialView: 'dayGridWeek',
                firstDay: 1,
                // Same remembered choice as the calendar page's "Show weekends" switch.
                weekends: readWeekendsPref(),
                headerToolbar: {
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridWeek,dayGridMonth'
                },
                buttonText: {
                    today: 'Today',
                    dayGridWeek: 'Week',
                    dayGridMonth: 'Month'
                },
                views: {
                    dayGridWeek: {
                        dayHeaderFormat: { weekday: 'short', day: 'numeric', omitCommas: true }
                    },
                    dayGridMonth: {},
                    timeGridDay: {
                        slotMinTime: '06:00:00',
                        slotMaxTime: '19:00:00',
                        slotDuration: '00:30:00',
                        slotLabelInterval: '01:00:00',
                        allDaySlot: true,
                        allDayText: 'All Day',
                        nowIndicator: true,
                        // The weekends switch is for the date grids only. A Day view that
                        // obeyed it would open a picked Saturday or Sunday on Monday.
                        weekends: true
                    }
                },
                height: 'auto',
                selectable: true,
                selectMirror: true,
                unselectAuto: false,
                editable: false,
                eventResizableFromStart: true,
                snapDuration: '00:30:00',
                validRange: {
                    start: toLocalDateStr(weekStart)
                },
                selectAllow: function(span) {
                    return span.start >= startOfToday;
                },
                events: '/api/v1/calendar/fullcalendar/events',
                eventColor: '#6c757d',
                eventDisplay: 'block',
                datesSet: function(info) {
                    // Leaving the day view discards any picked start time
                    // (parity with the old "back to month" button behaviour).
                    if (self._lastViewType === 'timeGridDay' && info.view.type !== 'timeGridDay') {
                        const timeSelect = document.getElementById('scheduleEventTime');
                        if (timeSelect) timeSelect.value = '';
                    }
                    self._lastViewType = info.view.type;

                    // Day view is one day, so the switch has nothing to do there.
                    const weekendToggle = document.getElementById('scheduleWeekendToggle');
                    if (weekendToggle) weekendToggle.hidden = !self.isDayGridView(info.view.type);

                    if (self.isDayGridView(info.view.type) && self.selectedDate) {
                        const daysRow = document.getElementById('daysRow');
                        if (daysRow) daysRow.style.display = 'flex';
                    }

                    if (info.view.type === 'timeGridDay') {
                        const newDateStr = info.startStr.split('T')[0];
                        if (newDateStr !== self.selectedDate) {
                            self.selectedDate = newDateStr;
                            const dateInput = document.getElementById('scheduleEventDate');
                            if (dateInput) dateInput.value = newDateStr;
                            const timeSelect = document.getElementById('scheduleEventTime');
                            if (timeSelect) timeSelect.value = '';
                        }
                    }

                    // Last, so the prompt reads the time select after the resets above.
                    self.updateStepPrompt();
                },
                dayCellDidMount: function(info) {
                    const cell = info.el;
                    // Past / out-of-range days are not selectable: no pointer
                    // affordance, no hover highlight (CSS dims them instead).
                    if (cell.classList.contains('fc-day-past') || cell.classList.contains('fc-day-disabled')) {
                        return;
                    }
                    cell.style.cursor = 'pointer';
                    cell.style.transition = 'background-color 0.15s ease';

                    cell.addEventListener('mouseenter', function() {
                        if (!cell.classList.contains('fc-day-selected')) {
                            cell.style.backgroundColor = '#e3f2fd';
                        }
                    });
                    cell.addEventListener('mouseleave', function() {
                        if (!cell.classList.contains('fc-day-selected')) {
                            cell.style.backgroundColor = '';
                        }
                    });
                },
                dateClick: function(info) {
                    if (self.isUpdatingSelection) return;

                    const currentView = self.calendar.view.type;

                    if (self.isDayGridView(currentView)) {
                        self.selectScheduleDate(info.dateStr, info.dayEl, true);
                    } else if (currentView === 'timeGridDay') {
                        const timeSelect = document.getElementById('scheduleEventTime');

                        if (info.allDay) {
                            if (timeSelect) {
                                timeSelect.value = '';
                                timeSelect.classList.add('border-success');
                                setTimeout(() => timeSelect.classList.remove('border-success'), 1000);
                            }
                            self.updateDurationVisibility();
                            self.clearScheduleSelection();
                            self.calendar.unselect();
                            return;
                        }

                        const clickedDateTime = new Date(info.dateStr);

                        const roundedMinutes = Math.round(clickedDateTime.getMinutes() / 30) * 30;
                        const roundedHours = clickedDateTime.getHours() + (roundedMinutes === 60 ? 1 : 0);
                        const roundedMins = roundedMinutes === 60 ? 0 : roundedMinutes;
                        const roundedTime = `${roundedHours.toString().padStart(2, '0')}:${roundedMins.toString().padStart(2, '0')}`;

                        if (timeSelect) {
                            const option = Array.from(timeSelect.options).find(opt => opt.value === roundedTime);
                            if (option) {
                                timeSelect.value = roundedTime;
                                timeSelect.classList.add('border-success');
                                setTimeout(() => timeSelect.classList.remove('border-success'), 1000);
                            }
                        }

                        self.updateDurationVisibility();
                        self.updateCalendarSelectionWithDuration(clickedDateTime);
                    }
                },
                select: function(info) {
                    if (self.isUpdatingSelection) return;

                    const currentView = self.calendar.view.type;

                    if (self.isDayGridView(currentView)) {
                        const dayEl = document.querySelector(`[data-date="${info.startStr}"]`);
                        self.selectScheduleDate(info.startStr, dayEl, true);
                    } else if (currentView === 'timeGridDay') {
                        const timeSelect = document.getElementById('scheduleEventTime');

                        if (info.allDay) {
                            if (timeSelect) {
                                timeSelect.value = '';
                                timeSelect.classList.add('border-success');
                                setTimeout(() => timeSelect.classList.remove('border-success'), 1000);
                            }
                            self.clearScheduleSelection();
                            self.updateDurationVisibility();
                            return;
                        }

                        self.setScheduleSelection(info.start, info.end);
                    }
                },
                eventDrop: function(info) {
                    if (info.event.id !== 'schedule-selection') { info.revert(); return; }
                    self.syncControlsFromSelection(info.event.start, info.event.end);
                },
                eventResize: function(info) {
                    if (info.event.id !== 'schedule-selection') { info.revert(); return; }
                    self.syncControlsFromSelection(info.event.start, info.event.end);
                },
                eventClick: function(info) {
                    info.jsEvent.preventDefault();

                    if (info.event.id === 'schedule-selection') return;

                    const currentView = self.calendar.view.type;
                    const isMobile = window.innerWidth < 768;

                    if (isMobile && self.isDayGridView(currentView)) {
                        const dayEl = info.el.closest('[data-date]');
                        if (dayEl) {
                            const dateStr = dayEl.dataset.date;
                            self.selectScheduleDate(dateStr, dayEl, true);
                        }
                        return;
                    }

                    self.showEventDetailsPanel(info.event);
                },
                eventDidMount: function(info) {
                    info.el.title = info.event.title;
                    info.el.style.cursor = 'pointer';

                    const eventQuoteId = info.event.extendedProps?.quote_id;
                    const eventJobId = info.event.extendedProps?.job_id;

                    let isCurrentEntity = false;
                    if (currentEntityType === 'quote' && eventQuoteId == currentEntityId) {
                        isCurrentEntity = true;
                    } else if (currentEntityType === 'job' && eventJobId == currentEntityId) {
                        isCurrentEntity = true;
                    }

                    if (isCurrentEntity) {
                        info.el.style.outline = '3px solid #ffd700';
                        info.el.style.outlineOffset = '1px';
                        info.el.style.boxShadow = '0 0 8px 2px rgba(255, 215, 0, 0.5)';
                        info.el.style.zIndex = '10';
                        info.el.title = '★ ' + info.event.title + ' (This ' + currentEntityType + ')';
                    }
                }
            });

            this.calendar.render();

            const weekendSwitch = document.getElementById('scheduleShowWeekends');
            if (weekendSwitch) {
                weekendSwitch.checked = this.calendar.getOption('weekends');
                // onchange, not addEventListener: every build rebinds the same element.
                weekendSwitch.onchange = () => {
                    this.calendar.setOption('weekends', weekendSwitch.checked);
                    rememberWeekendsPref(weekendSwitch.checked);
                };
            }
        }

        /** Handle date selection with visual feedback. */
        selectScheduleDate(dateStr, dayEl, switchToDayView = false) {
            // Single choke point for every selection path (dateClick, select,
            // mobile eventClick, reschedule): never select a past date.
            const now = new Date();
            const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
            if (dateStr < todayStr) return;

            this.selectedDate = dateStr;

            const dateInput = document.getElementById('scheduleEventDate');
            if (dateInput) dateInput.value = dateStr;

            const eventClickTip = document.getElementById('eventClickTip');
            if (eventClickTip) eventClickTip.style.display = 'none';

            const timeOptionsRow = document.getElementById('timeOptionsRow');
            if (timeOptionsRow) timeOptionsRow.style.display = 'flex';

            const daysRow = document.getElementById('daysRow');
            if (daysRow) daysRow.style.display = 'flex';

            const notesRow = document.getElementById('notesRow');
            if (notesRow) notesRow.style.display = 'block';

            this.updateMultiDayPreview();
            this.updateDurationVisibility();

            const submitBtn = document.getElementById('submitScheduleEventBtn');
            if (submitBtn) submitBtn.disabled = false;

            // Visual feedback: highlight selected date (only in month view)
            document.querySelectorAll('.fc-day-selected').forEach(function(el) {
                el.classList.remove('fc-day-selected');
                el.style.backgroundColor = '';
                const inner = el.querySelector('.fc-daygrid-day-top');
                if (inner) inner.style.color = '';
                const num = el.querySelector('.fc-daygrid-day-number');
                if (num) num.style.color = '';
            });

            if (dayEl) {
                dayEl.classList.add('fc-day-selected');
                dayEl.style.backgroundColor = '#0d6efd';
                const inner = dayEl.querySelector('.fc-daygrid-day-top');
                if (inner) inner.style.color = 'white';
                const num = dayEl.querySelector('.fc-daygrid-day-number');
                if (num) num.style.color = 'white';
            }

            if (switchToDayView && this.calendar) {
                setTimeout(() => {
                    this.calendar.changeView('timeGridDay', dateStr);
                    this._showCarriedTime(dateStr);
                }, 150);
            }
        }

        /**
         * On entering a day, draw the start time already in the controls as the
         * selection block. Only a reschedule arrives with one (_prefillVisitTime);
         * a new booking picks its day before any time.
         */
        _showCarriedTime(dateStr) {
            const time = document.getElementById('scheduleEventTime')?.value;
            if (time) this.updateCalendarSelectionWithDuration(new Date(`${dateStr}T${time}:00`));
        }

        /** Delete the current event. */
        async deleteScheduleEvent() {
            const eventIdInput = document.getElementById('currentEventId');
            const eventId = eventIdInput?.value;

            if (!eventId || this.isDeleting) {
                return;
            }

            if (!confirm('Are you sure you want to delete this event?')) {
                return;
            }

            this.isDeleting = true;
            const eventIdToDelete = eventId;

            if (eventIdInput) eventIdInput.value = '';

            const deleteBtn = document.getElementById('deleteEventBtn');
            if (deleteBtn) {
                deleteBtn.disabled = true;
                deleteBtn.innerHTML = '<span class="spinner-border spinner-border-sm"></span>';
            }

            try {
                const response = await fetch(`/calendar/event/${eventIdToDelete}`, {
                    method: 'DELETE',
                    headers: scheduleMutationHeaders()
                });

                if (response.ok) {
                    const scheduleModal = document.getElementById('scheduleEventModal');
                    const modal = bootstrap.Modal.getInstance(scheduleModal);
                    if (modal) modal.hide();

                    const contentType = response.headers.get('Content-Type') || '';
                    if (contentType.includes('turbo-stream')) {
                        const streamHtml = await response.text();
                        if (typeof Turbo !== 'undefined' && Turbo.renderStreamMessage) {
                            Turbo.renderStreamMessage(streamHtml);
                        }
                    }

                    document.dispatchEvent(new CustomEvent('schedule:changed', {
                        detail: {
                            entityType: this.currentEntityType,
                            entityId: this.currentEntityId,
                            action: 'delete'
                        }
                    }));

                    if (typeof window.showNotification === 'function') {
                        window.showNotification('Event deleted successfully!', 'success');
                    }
                } else {
                    const data = await response.json();
                    if (typeof window.showNotification === 'function') {
                        window.showNotification(data.error || 'Failed to delete event', 'error');
                    } else {
                        alert(data.error || 'Failed to delete event');
                    }
                }
            } catch (error) {
                console.error('Error deleting event:', error);
                if (typeof window.showNotification === 'function') {
                    window.showNotification('Failed to delete event', 'error');
                } else {
                    alert('Failed to delete event');
                }
            } finally {
                this.isDeleting = false;
                if (deleteBtn) {
                    deleteBtn.disabled = false;
                    deleteBtn.innerHTML = '<i class="bi bi-trash me-1"></i>Delete';
                }
            }
        }

        /** Start reschedule mode - go back to calendar to pick new date. */
        startRescheduleEvent() {
            const event = this.currentEvent;
            if (!event) return;

            const editEventIdEl = document.getElementById('scheduleEditEventId');
            if (editEventIdEl) editEventIdEl.value = event.id;

            // The open cleared the picker; the save sends whatever it holds as
            // the visit's crew, so it starts from the crew already on it.
            seedAssigneePicker(
                document.getElementById('scheduleEventModal'),
                crewFromAssignees(event.extendedProps?.assignees),
            );

            const modalTitle = document.getElementById('scheduleEventModalLabel');
            if (modalTitle) {
                modalTitle.innerHTML = '<i class="bi bi-calendar-week me-2"></i>Reschedule Event';
            }

            const submitBtn = document.getElementById('submitScheduleEventBtn');
            if (submitBtn) {
                submitBtn.innerHTML = '<i class="bi bi-calendar-check me-1"></i>Update';
            }

            this.showScheduleFormPanel();
            this.updateStepPrompt();

            if (event.start) {
                // Local calendar date: a UTC conversion moves visits before the
                // viewer's UTC offset (all-day ones start at local midnight) a day early.
                const dateStr = formatLocalDatetime(new Date(event.start)).split('T')[0];

                // Navigate first: the week view rarely contains the event's
                // date, and an off-screen date has no [data-date] cell at all.
                if (this.calendar) this.calendar.gotoDate(dateStr);

                setTimeout(() => {
                    const dayEl = document.querySelector(`[data-date="${dateStr}"]`);
                    if (dayEl) {
                        this.selectScheduleDate(dateStr, dayEl);
                        this._prefillVisitTime(event, dateStr);
                    }
                }, 100);
            }
        }

        /**
         * Carry a timed visit's start and length into the controls, so
         * confirming without a change keeps the visit as it is and picking a
         * new day opens that day with the same slot selected. The calendar
         * stays on the date pick ("Pick a new date"). An all-day or multi-day
         * visit, or one on a past date (selectScheduleDate refuses those), has
         * nothing to carry.
         */
        _prefillVisitTime(event, dateStr) {
            if (event.allDay || !event.end || this.selectedDate !== dateStr) return;
            const start = new Date(event.start);
            const end = new Date(event.end);
            if (formatLocalDatetime(end).split('T')[0] !== dateStr) return;
            const slotStart = roundToHalfHour(start);
            this.syncControlsFromSelection(slotStart, new Date(slotStart.getTime() + (end - start)));
        }

        /**
         * Open straight into reschedule mode for one visit. The event modal's
         * Reschedule hands off here by dispatching `schedule-modal:reschedule`
         * on this element with detail { visit } (the calendar feed's shape).
         * The calendar is only built once the modal is shown, so the reschedule
         * itself starts from the shown.bs.modal handler.
         */
        rescheduleVisit({ detail: { visit } }) {
            const props = visit.extendedProps || {};
            const entityType = props.job_id ? 'job' : (props.quote_id ? 'quote' : '');
            this._pendingReschedule = visit;
            this.openScheduleModal(entityType, props.job_id || props.quote_id || 0, props.client_id || 0,
                props.client_name, props.location || '', visit.title, props.duration_minutes);
        }

        /**
         * Pick a day and start time as a tap on the calendar would: the
         * walkthrough's schedule step dispatches `schedule-modal:pick` on this
         * element with detail { date: 'YYYY-MM-DD', time: 'HH:MM' }. The
         * calendar is built only once the modal is shown, so a pick that
         * arrives first waits for it.
         */
        pickVisit({ detail: { date, time } }) {
            this._pendingPick = { date, time };
            if (this.calendar) this._applyPendingPick();
        }

        _applyPendingPick() {
            const pick = this._pendingPick;
            if (!pick) return;
            this._pendingPick = null;
            const timeSelect = document.getElementById('scheduleEventTime');
            if (timeSelect && Array.from(timeSelect.options).some((opt) => opt.value === pick.time)) {
                timeSelect.value = pick.time;
            }
            this.selectScheduleDate(pick.date, null, true);
        }

        /** Enter reschedule mode for the visit rescheduleVisit left waiting, if any. */
        _resumePendingReschedule() {
            const visit = this._pendingReschedule;
            if (!visit) return;
            this._pendingReschedule = null;
            this.currentEvent = visit;
            this.startRescheduleEvent();
        }

        /** Submit the schedule event form (create or reschedule). */
        async submitScheduleEvent() {
            if (!this.selectedDate) {
                alert('Please select a date');
                return;
            }

            const submitBtn = document.getElementById('submitScheduleEventBtn');
            if (!submitBtn) return;

            const originalText = submitBtn.innerHTML;
            submitBtn.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Saving...';
            submitBtn.disabled = true;

            try {
                const getVal = (id) => {
                    const el = document.getElementById(id);
                    return el ? el.value : '';
                };

                const editEventId = getVal('scheduleEditEventId');
                const isReschedule = !!editEventId;

                const entityType = getVal('scheduleEntityType');
                const entityId = parseInt(getVal('scheduleEntityId')) || 0;
                const clientId = parseInt(getVal('scheduleClientId')) || 0;
                const title = getVal('scheduleEventTitle');
                const eventTime = getVal('scheduleEventTime');
                const durationMinutes = parseInt(getVal('scheduleEventDuration')) || 480;
                const numDays = parseInt(document.getElementById('scheduleEventDays')?.value) || 1;
                const location = getVal('scheduleEventLocation');
                const description = getVal('scheduleEventDescription');

                const { startDatetime, endDatetime, allDay } = visitTiming({
                    selectedDate: this.selectedDate,
                    eventTime,
                    durationMinutes,
                    numDays,
                    selection: this.selectionEvent,
                });

                let response;
                let url;
                let body;

                // Scoped to THIS modal: the calendar page's quick-schedule
                // modal is in the DOM too and carries its own picker.
                const crew = readAssigneeSelection(document.getElementById('scheduleEventModal'));

                if (isReschedule) {
                    url = `/calendar/event/${editEventId}/reschedule`;
                    body = JSON.stringify({
                        start_datetime: startDatetime,
                        end_datetime: endDatetime,
                        all_day: allDay,
                        ...crew
                    });
                } else {
                    url = '/calendar/event';
                    const requestBody = {
                        title: title,
                        description: description || null,
                        start_datetime: startDatetime,
                        end_datetime: endDatetime,
                        event_type: entityType,
                        location: location || null,
                        client_id: clientId,
                        all_day: allDay,
                        ...crew
                    };

                    if (entityType === 'quote') {
                        requestBody.quote_id = entityId;
                    } else if (entityType === 'job') {
                        requestBody.job_id = entityId;
                    }
                    body = JSON.stringify(requestBody);
                }

                response = await fetch(url, {
                    method: 'POST',
                    headers: scheduleMutationHeaders(),
                    body: body
                });

                if (response.ok) {
                    const scheduleModal = document.getElementById('scheduleEventModal');
                    const modal = bootstrap.Modal.getInstance(scheduleModal);
                    if (modal) modal.hide();

                    const contentType = response.headers.get('Content-Type') || '';
                    if (contentType.includes('turbo-stream')) {
                        const streamHtml = await response.text();
                        if (typeof Turbo !== 'undefined' && Turbo.renderStreamMessage) {
                            Turbo.renderStreamMessage(streamHtml);
                        }
                    }

                    document.dispatchEvent(new CustomEvent('schedule:changed', {
                        detail: { entityType, entityId, action: isReschedule ? 'reschedule' : 'create' }
                    }));

                    const message = isReschedule ? 'Event rescheduled successfully!' : 'Event scheduled successfully!';
                    if (typeof window.showNotification === 'function') {
                        window.showNotification(message, 'success');
                    }
                } else {
                    const data = await response.json();
                    const errMsg = window.FormError
                        ? window.FormError.extract(data, 'Failed to save event')
                        : (data.error || data.message || 'Failed to save event');
                    if (typeof window.showNotification === 'function') {
                        window.showNotification(errMsg, 'error');
                    } else {
                        alert('Error: ' + errMsg);
                    }
                }
            } catch (error) {
                console.error('Error saving event:', error);
                if (typeof window.showNotification === 'function') {
                    window.showNotification('Failed to save event. Please try again.', 'error');
                } else {
                    alert('Failed to save event. Please try again.');
                }
            } finally {
                submitBtn.innerHTML = originalText;
                submitBtn.disabled = false;
            }
        }

        /** Open modal from scheduled badge click - fetches entity details via API. */
        async openScheduleModalFromBadge(entityType, entityId) {
            try {
                const endpoint = entityType === 'quote' ? `/api/v1/quotes/${entityId}` : `/api/v1/jobs/${entityId}`;
                let data;
                try {
                    data = await window.api(endpoint, { notifyError: false });
                } catch (err) {
                    console.error('[Schedule] API request failed:', err && err.status);
                    this.openScheduleModal(entityType, entityId, 0, 'Client', '', '', 480);
                    return;
                }
                const entity = data.data || data;

                let clientName = 'Client';
                if (entity.client) {
                    clientName = entity.client.display_name || entity.client.name ||
                                `${entity.client.first_name || ''} ${entity.client.last_name || ''}`.trim() || 'Client';
                } else if (entity.client_name) {
                    clientName = entity.client_name;
                }

                const clientId = entity.client_id || entity.client?.id || 0;
                const location = entity.location || entity.job_address || entity.address || '';

                let title = '';
                if (entityType === 'quote') {
                    title = `Site Visit - ${clientName}`;
                } else if (entityType === 'job') {
                    title = eventTitleFor('job', entityId, clientName);
                }

                const duration = entityType === 'quote' ? 60 : 480;

                this.openScheduleModal(entityType, entityId, clientId, clientName, location, title, duration);
            } catch (error) {
                console.error('[Schedule] Error fetching entity details:', error);
                this.openScheduleModal(entityType, entityId, 0, 'Client', '', '', 480);
            }
        }

        /**
         * Wire the modal's shown/hidden lifecycle and its input controls. Runs once
         * per controller connect; the modal element is fresh after each Turbo body
         * swap, so the per-element guard flags reset naturally.
         */
        initScheduleModalButtons() {
            const self = this;
            const scheduleModal = document.getElementById('scheduleEventModal');
            if (!scheduleModal) {
                return;
            }

            // Build calendar when modal is shown; tear down when hidden.
            if (!scheduleModal._scheduleShownAttached) {
                scheduleModal._scheduleShownAttached = true;
                scheduleModal.addEventListener('shown.bs.modal', function() {
                    self._whenCalendarLoaded(scheduleModal, function() {
                        self.initScheduleCalendar();
                        self._resumePendingReschedule();
                        self._applyPendingPick();
                    });
                });

                scheduleModal.addEventListener('hidden.bs.modal', function() {
                    self._destroyCalendar();
                    self.selectedDate = null;
                    self.currentEvent = null;
                    self.previewEvent = null;   // calendar destroyed, reference is stale
                    self.selectionEvent = null; // calendar destroyed, reference is stale

                    const calendarEl = document.getElementById('scheduleCalendar');
                    if (calendarEl) {
                        calendarEl.innerHTML = '';
                    }

                    self.showScheduleFormPanel();
                });
            }

            // Submit/back/delete/reschedule buttons use data-action="call-fn" in the
            // template (routed by global-actions.js to the window.* entry points).

            const durationSelect = document.getElementById('scheduleEventDuration');
            if (durationSelect && !durationSelect._scheduleAttached) {
                durationSelect._scheduleAttached = true;
                durationSelect.addEventListener('change', function() {
                    self.updateFooterDurationText();

                    const timeSelect = document.getElementById('scheduleEventTime');
                    if (self.calendar &&
                        self.calendar.view.type === 'timeGridDay' &&
                        timeSelect && timeSelect.value) {
                        const dateStr = self.selectedDate;
                        const timeStr = timeSelect.value;
                        const startDateTime = new Date(`${dateStr}T${timeStr}:00`);
                        self.updateCalendarSelectionWithDuration(startDateTime);
                    }
                });
            }

            // The footer's duration dial is its own controller
            // (duration_dial_controller.js); it writes #scheduleEventDuration
            // and fires the change handled above.

            const daysInput = document.getElementById('scheduleEventDays');
            if (daysInput && !daysInput._scheduleAttached) {
                daysInput._scheduleAttached = true;
                daysInput.addEventListener('input', function() {
                    const prev = parseInt(this.dataset.prevDays || 1);
                    const cur = parseInt(this.value) || 1;
                    if (prev <= 1 && cur > 1 &&
                        self.calendar &&
                        self.calendar.view.type === 'timeGridDay') {
                        self.calendar.changeView('dayGridMonth');
                    }
                    this.dataset.prevDays = cur;
                    self.updateDurationVisibility();
                    self.updateMultiDayPreview();
                });
                daysInput.addEventListener('change', function() {
                    self.updateDurationVisibility();
                    self.updateMultiDayPreview();
                });
            }

            const daysDecrBtn = document.getElementById('daysDecrBtn');
            if (daysDecrBtn && !daysDecrBtn._scheduleAttached) {
                daysDecrBtn._scheduleAttached = true;
                daysDecrBtn.addEventListener('click', function() {
                    const input = document.getElementById('scheduleEventDays');
                    if (input) {
                        input.value = Math.max(1, (parseInt(input.value) || 1) - 1);
                        self.updateDurationVisibility();
                        self.updateMultiDayPreview();
                    }
                });
            }

            const daysIncrBtn = document.getElementById('daysIncrBtn');
            if (daysIncrBtn && !daysIncrBtn._scheduleAttached) {
                daysIncrBtn._scheduleAttached = true;
                daysIncrBtn.addEventListener('click', function() {
                    const input = document.getElementById('scheduleEventDays');
                    if (input) {
                        const prev = parseInt(input.value) || 1;
                        input.value = prev + 1;
                        if (prev === 1 &&
                            self.calendar &&
                            self.calendar.view.type === 'timeGridDay') {
                            self.calendar.changeView('dayGridMonth');
                        }
                        self.updateDurationVisibility();
                        self.updateMultiDayPreview();
                    }
                });
            }

            const timeSelect = document.getElementById('scheduleEventTime');
            if (timeSelect && !timeSelect._scheduleAttached) {
                timeSelect._scheduleAttached = true;
                timeSelect.addEventListener('change', function() {
                    self.updateDurationVisibility();

                    const footerBtn = document.querySelector('#footerDurationGroup button');
                    if (footerBtn && timeSelect.value) {
                        footerBtn.classList.add('dropdown-toggle');
                    }

                    if (!timeSelect.value) {
                        self.clearScheduleSelection();
                    } else if (self.calendar &&
                        self.calendar.view.type === 'timeGridDay') {
                        const dateStr = self.selectedDate;
                        const timeStr = timeSelect.value;
                        const startDateTime = new Date(`${dateStr}T${timeStr}:00`);
                        self.updateCalendarSelectionWithDuration(startDateTime);
                    }
                });
            }
        }

        /**
         * Document-delegated click handler for schedule triggers
         * ([data-schedule-action="open"] with data-schedule-* context).
         */
        handleScheduleClick(e) {
            const scheduleBtn = e.target.closest('[data-schedule-action="open"]');
            if (scheduleBtn) {
                e.preventDefault();
                e.stopPropagation();
                const entityType = scheduleBtn.dataset.scheduleEntityType;
                const entityId = parseInt(scheduleBtn.dataset.scheduleEntityId);
                const clientId = parseInt(scheduleBtn.dataset.scheduleClientId) || 0;
                const clientName = scheduleBtn.dataset.scheduleClientName || 'Client';
                const location = scheduleBtn.dataset.scheduleLocation || '';
                const title = scheduleBtn.dataset.scheduleTitle || '';
                const duration = parseInt(scheduleBtn.dataset.scheduleDuration) || 480;
                this.openScheduleModal(entityType, entityId, clientId, clientName, location, title, duration);
                return;
            }
        }
}
